// Mercapoli - Gestión de Promociones del Comercio
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Tag, Calendar, Clock, Percent,
  DollarSign, Trash2, ToggleLeft, ToggleRight, Edit
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { comercio as comercioApi } from '../../lib/api';
import { toast } from 'sonner';
import MerchantHeader from '../../components/merchant/MerchantHeader';


const diasSemana = [
  { id: 0, nombre: 'Lunes' },
  { id: 1, nombre: 'Martes' },
  { id: 2, nombre: 'Miércoles' },
  { id: 3, nombre: 'Jueves' },
  { id: 4, nombre: 'Viernes' },
  { id: 5, nombre: 'Sábado' },
  { id: 6, nombre: 'Domingo' },
];

const MerchantPromociones = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [promociones, setPromociones] = useState([]);
  const [productos, setProductos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    tipo: 'porcentaje',
    valor: '',
    productos_ids: [],
    fecha_inicio: '',
    fecha_fin: '',
    horario_inicio: '',
    horario_fin: '',
    dias_semana: [0, 1, 2, 3, 4, 5, 6],
    activa: true,
    min_compra: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [promoData, productosData] = await Promise.all([
        comercioApi.getPromociones(),
        comercioApi.getProductos()
      ]);
      setPromociones(promoData);
      setProductos(productosData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar promociones');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.valor || !formData.fecha_inicio || !formData.fecha_fin) {
      toast.error('Completa los campos requeridos');
      return;
    }

    try {
      const data = {
        ...formData,
        valor: parseFloat(formData.valor),
        min_compra: formData.min_compra ? parseFloat(formData.min_compra) : 0,
        fecha_inicio: new Date(formData.fecha_inicio).toISOString(),
        fecha_fin: new Date(formData.fecha_fin).toISOString()
      };

      await comercioApi.createPromocion(data);
      toast.success('Promoción creada');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al crear promoción');
    }
  };

  const handleToggle = async (promocionId, activa) => {
    try {
      await comercioApi.togglePromocion(promocionId, !activa);
      toast.success(`Promoción ${!activa ? 'activada' : 'desactivada'}`);
      loadData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async (promocionId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta promoción?')) return;
    
    try {
      await comercioApi.deletePromocion(promocionId);
      toast.success('Promoción eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar promoción');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      tipo: 'porcentaje',
      valor: '',
      productos_ids: [],
      fecha_inicio: '',
      fecha_fin: '',
      horario_inicio: '',
      horario_fin: '',
      dias_semana: [0, 1, 2, 3, 4, 5, 6],
      activa: true,
      min_compra: ''
    });
  };

  const toggleDia = (diaId) => {
    const newDias = formData.dias_semana.includes(diaId)
      ? formData.dias_semana.filter(d => d !== diaId)
      : [...formData.dias_semana, diaId].sort();
    setFormData({ ...formData, dias_semana: newDias });
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    return new Date(fecha).toLocaleDateString('es-GT', { 
      day: '2-digit', month: 'short', year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header con Logout */}
      <MerchantHeader 
        title="Promociones" 
        rightContent={
          <Button onClick={() => { resetForm(); setShowModal(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Nueva
          </Button>
        }
      />

      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{promociones.length}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">
                {promociones.filter(p => p.esta_vigente).length}
              </p>
              <p className="text-xs text-muted-foreground">Vigentes</p>
            </CardContent>
          </Card>
        </div>

        {/* Promociones List */}
        {promociones.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Tag className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No hay promociones</p>
              <Button className="mt-4" onClick={() => { resetForm(); setShowModal(true); }}>
                <Plus className="h-4 w-4 mr-2" /> Crear primera promoción
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {promociones.map((promo) => (
                <motion.div
                  key={promo.promocion_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <Card className={`${!promo.activa ? 'opacity-60' : ''} ${promo.esta_vigente ? 'border-green-500' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-bold">{promo.nombre}</h3>
                            {promo.esta_vigente ? (
                              <Badge className="bg-green-500">Vigente</Badge>
                            ) : promo.activa ? (
                              <Badge variant="outline">Programada</Badge>
                            ) : (
                              <Badge variant="secondary">Inactiva</Badge>
                            )}
                          </div>
                          
                          <p className="text-sm text-muted-foreground mb-3">{promo.descripcion}</p>
                          
                          <div className="flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-1">
                              {promo.tipo === 'porcentaje' ? (
                                <Percent className="h-4 w-4 text-orange-500" />
                              ) : (
                                <DollarSign className="h-4 w-4 text-green-500" />
                              )}
                              <span className="font-medium">
                                {promo.tipo === 'porcentaje' ? `${promo.valor}%` : `Q${promo.valor}`}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Calendar className="h-4 w-4" />
                              <span>{formatFecha(promo.fecha_inicio)} - {formatFecha(promo.fecha_fin)}</span>
                            </div>
                            
                            {promo.horario_inicio && promo.horario_fin && (
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Clock className="h-4 w-4" />
                                <span>{promo.horario_inicio} - {promo.horario_fin}</span>
                              </div>
                            )}
                          </div>

                          {promo.dias_semana?.length < 7 && (
                            <div className="flex gap-1 mt-2">
                              {diasSemana.map((dia) => (
                                <Badge
                                  key={dia.id}
                                  variant={promo.dias_semana?.includes(dia.id) ? 'default' : 'outline'}
                                  className="text-xs"
                                >
                                  {dia.nombre.slice(0, 2)}
                                </Badge>
                              ))}
                            </div>
                          )}

                          {promo.min_compra > 0 && (
                            <p className="text-xs text-muted-foreground mt-2">
                              Mínimo de compra: Q{promo.min_compra}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggle(promo.promocion_id, promo.activa)}
                          >
                            {promo.activa ? (
                              <ToggleRight className="h-6 w-6 text-green-500" />
                            ) : (
                              <ToggleLeft className="h-6 w-6 text-muted-foreground" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500"
                            onClick={() => handleDelete(promo.promocion_id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Promoción</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ej: 20% en toda la tienda"
                required
              />
            </div>

            <div>
              <Label>Descripción</Label>
              <Input
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Descripción de la promoción..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo *</Label>
                <Select
                  value={formData.tipo}
                  onValueChange={(v) => setFormData({ ...formData, tipo: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="porcentaje">Porcentaje (%)</SelectItem>
                    <SelectItem value="monto_fijo">Monto Fijo (Q)</SelectItem>
                    <SelectItem value="2x1">2x1</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.valor}
                  onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                  placeholder={formData.tipo === 'porcentaje' ? '20' : '10.00'}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha Inicio *</Label>
                <Input
                  type="datetime-local"
                  value={formData.fecha_inicio}
                  onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Fecha Fin *</Label>
                <Input
                  type="datetime-local"
                  value={formData.fecha_fin}
                  onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Horario Inicio (opcional)</Label>
                <Input
                  type="time"
                  value={formData.horario_inicio}
                  onChange={(e) => setFormData({ ...formData, horario_inicio: e.target.value })}
                />
              </div>
              <div>
                <Label>Horario Fin (opcional)</Label>
                <Input
                  type="time"
                  value={formData.horario_fin}
                  onChange={(e) => setFormData({ ...formData, horario_fin: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Días de la Semana</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {diasSemana.map((dia) => (
                  <Badge
                    key={dia.id}
                    variant={formData.dias_semana.includes(dia.id) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleDia(dia.id)}
                  >
                    {dia.nombre}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Mínimo de Compra (opcional)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.min_compra}
                onChange={(e) => setFormData({ ...formData, min_compra: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit">Crear Promoción</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerchantPromociones;
